import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';
import { SubscriptionDetailsConfig, SubscriptionDetail } from './subscription-details.model';
import { SubscriptionService } from 'src/app/services/subscription/subscription.service';
import { CommonService } from 'src/app/services/Common-function/common.service';
import { CreditQuotaHelper } from 'src/app/constants/credit-quota.constants';
import { CurrentActivatedPlan } from 'src/app/models/activated-subscription.model';

@Component({
    selector: 'app-subscription-details',
    templateUrl: './subscription-details.component.html',
    styleUrls: ['./subscription-details.component.scss']
})
export class SubscriptionDetailsComponent implements OnInit, OnDestroy {

    // Configuration Input
    @Input() config: SubscriptionDetailsConfig;
    @Input() currentTier: string = "";

    // Display mode
    @Input() mode: 'dropdown' | 'bottomsheet' = 'dropdown';

    // Commission flag - determines color variant for premium/pro tiers
    @Input() isCommission: boolean = false;

    // Close event for bottom sheet
    @Output() onClose = new EventEmitter<void>();
    

    // Dynamic data
    details: SubscriptionDetail[] = [];
    isLoading = false;

    private destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        private subscriptionService: SubscriptionService,
        private commonService: CommonService
    ) { }

    ngOnInit() {
        if (!this.config) return;
        this.loadSubscriptionData();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Subscribe to the shared activeSubscription BehaviorSubject
     * Both dropdown and bottom sheet use the same cached data source,
     */
    loadSubscriptionData() {
        this.isLoading = true;
        this.subscriptionService.activeSubscription
            .pipe(
                takeUntil(this.destroy$),
                filter((data: any) => data && typeof data === 'object' && Object.keys(data).length > 0)
            )
            .subscribe({
                next: (data: CurrentActivatedPlan) => {
                    this.details = this.mapApiDataToDetails(data);
                    this.isLoading = false;
                },
                error: (err: any) => {
                    console.error('Failed to load subscription data:', err);
                    this.details = [];
                    this.isLoading = false;
                }
            });
    }

    /**
     * Map subscription data from the shared BehaviorSubject into display rows.
     * Commission is hardcoded only for tier0/basic (30%) since API doesn't return it.
     */
    mapApiDataToDetails(data: any): SubscriptionDetail[] {
        const tier = data.subscriptionTiers?.[data.subscriptionTiers.length - 1] || 'tier0';
        // Use the last (most recent) active subscription — after an upgrade, the new plan is appended
        const latestSub = data.activeSubscriptions?.[data.activeSubscriptions?.length - 1];
        const frequency = latestSub?.frequency || 'monthly';
        const sellerCommissionPercent: number = data.sellerCommissionPercent ?? 0;
        const isCommission = sellerCommissionPercent > 0;
        const quota = data.credits?.quota || CreditQuotaHelper.getQuota(tier, frequency, isCommission);
        const balance = data.credits?.balance || 0;
        const renewalDate = latestSub?.nextBillingDueAt;

        const details: SubscriptionDetail[] = [
            {
                label: 'Beat Uploads:',
                value: tier === 'tier0' ? '10' : 'Unlimited',
            },
            {
                label: 'Sound Kit Uploads:',
                value: tier === 'tier0' ? '5 Uploads' : 'Unlimited',
            },
            {
                label: 'Available Credits:',
                value: `${balance} Credits`,
                icon: './assets/imgs/icons/credits.svg'
            },
            {
                label: 'Total Credits:',
                value: tier === 'tier0'
                    ? '0 Credits'
                    : `${balance} Credits / ${frequency === 'monthly' ? 'month' : 'year'}`
            },
        ];

        // Commission Charged row: show for tier0 (hardcoded 30%) or when API returns > 0
        if (tier === 'tier0') {
            details.push({
                label: 'Commission Charged:',
                value: '30%'
            });
        } else if (sellerCommissionPercent > 0) {
            details.push({
                label: 'Commission Charged:',
                value: `${sellerCommissionPercent}%`
            });
        }

        // Renews On: only show for paid plans with an active subscription
        if (tier !== 'tier0' && renewalDate) {
            details.push({
                label: 'Renews On:',
                value: this.formatDate(renewalDate)
            });
        }

        return details;
    }

    formatDate(isoDate: string): string {
        if (!isoDate) return 'N/A';
        const date = new Date(isoDate);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    }

    onButtonClick(event?: Event) {
        if (event) {
            event.stopPropagation();
        }

        // Close bottom sheet if in mobile mode
        if (this.mode === 'bottomsheet') {
            this.close();
        }

        // Navigate to subscriptions page
        this.router.navigate([this.config.buttonRoute]);
    }

    close() {
        // Close bottom sheet using common service
        if (this.commonService.bottomRef) {
            this.commonService.bottomRef.dismiss();
        }
        this.onClose.emit();
    }
}
